import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const CookiePolicy = () => {
  return (
    <>
      <SEO
        title="Cookie Policy - ANIMAC Entertainment Hub"
        description="ANIMAC Cookie Policy - Learn about how we use cookies and similar technologies."
        url="/cookies"
        type="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=80" 
              alt="Cookie Policy" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-azonix font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Cookie Policy
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                This policy explains how ANIMAC uses cookies and similar technologies to enhance your experience.
              </p>
              <p className="text-gray-500 text-sm mt-4">Last updated: March 2025</p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="space-y-8">
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">1. What Are Cookies</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Cookies are small text files that are stored on your computer or mobile device when you visit a website. They allow the website to recognize your device and remember information about your visit.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">2. Types of Cookies We Use</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">We use the following types of cookies:</p>
                  
                  <div className="space-y-4">
                    <div className="border-l-4 border-cyan-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Essential Cookies</h3>
                      <p className="text-gray-400 text-sm">
                        These cookies are necessary for the website to function. They enable basic features like page navigation and access to secure areas. The website cannot function without these cookies.
                      </p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Performance Cookies</h3>
                      <p className="text-gray-400 text-sm">
                        These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the site functionality.
                      </p>
                    </div>
                    <div className="border-l-4 border-purple-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Functional Cookies</h3>
                      <p className="text-gray-400 text-sm">
                        These cookies allow the website to remember choices you make and provide enhanced, more personalized features.
                      </p>
                    </div>
                    <div className="border-l-4 border-pink-500 pl-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Targeting Cookies</h3>
                      <p className="text-gray-400 text-sm">
                        These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant content.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">3. How We Use Cookies</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">We use cookies for the following purposes:</p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>To keep you signed in</li>
                    <li>To remember your preferences and settings</li>
                    <li>To analyze traffic and improve our services</li>
                    <li>To personalize content and advertisements</li>
                    <li>To provide social media features</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">4. Third-Party Cookies</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. The third parties include analytics providers and advertising networks.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">5. Managing Cookies</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences in the following ways:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Browser settings - Most web browsers allow you to control cookies through their settings</li>
                    <li>Third-party tools - You can opt out of certain tracking via tools like the Digital Advertising Alliance</li>
                    <li>Our preference center - Where available, you can manage your preferences</li>
                  </ul>
                  <p className="text-gray-400 text-sm mt-4">
                    Note: If you disable essential cookies, some parts of our service may not function properly.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">6. Cookie List</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">Below is a list of the main cookies we use:</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-400">
                      <thead className="text-xs uppercase text-gray-500 border-b border-gray-700">
                        <tr>
                          <th className="py-3">Cookie Name</th>
                          <th className="py-3">Type</th>
                          <th className="py-3">Purpose</th>
                          <th className="py-3">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        <tr>
                          <td className="py-3 text-white">session_id</td>
                          <td className="py-3">Essential</td>
                          <td className="py-3">User session management</td>
                          <td className="py-3">Session</td>
                        </tr>
                        <tr>
                          <td className="py-3 text-white">preferences</td>
                          <td className="py-3">Functional</td>
                          <td className="py-3">User preferences storage</td>
                          <td className="py-3">1 year</td>
                        </tr>
                        <tr>
                          <td className="py-3 text-white">_ga</td>
                          <td className="py-3">Performance</td>
                          <td className="py-3">Google Analytics tracking</td>
                          <td className="py-3">2 years</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">7. Updates to This Policy</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We may update this Cookie Policy from time to time to reflect changes in our practices. We will notify you of any material changes by posting the updated policy on this page.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">8. Contact Us</h2>
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions about our Cookie Policy, please contact us at iconanimac@gmail.com.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CookiePolicy;